import {SLQueryParamConfig} from '#/contexts/query-params-provider';
import {WalletContext} from '#/contexts/wallet-provider';
import {LocalStorageKeys} from '#/environment/environment';
import {DefaultChildren} from '#/utils/utils';
import {createContext, Dispatch, FunctionComponent, SetStateAction, useContext, useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {useLocalStorage} from 'react-use';
import {useQueryParams} from 'use-query-params';

const isIOSStandalone = () => {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  return isIOS && !window.ethereum && !(window.navigator as unknown as {standalone: boolean}).standalone;
};

export interface UIStatusContextValue {
  isIOSStandalone: boolean;
  showInstallAsStandaloneIOS: boolean;
  setShowInstallAsStandaloneIOS: (value: boolean) => void;
  showSignAppWithWallet: boolean;
  setShowSignAppWithWallet: Dispatch<SetStateAction<boolean>>;
  isActionMenuOpen: boolean;
  isMenuOpen: boolean;
  toggleMenu: (value?: boolean) => void;
}

const initialValue: UIStatusContextValue = {} as UIStatusContextValue;
export const UIStatusContext = createContext<UIStatusContextValue>(initialValue);

export const UIStatusProvider: FunctionComponent<DefaultChildren> = ({children}) => {
  // External hooks
  const location = useLocation();
  const {accountResult, web3AuthSignatureResult} = useContext(WalletContext);
  const [{landToken, action}] = useQueryParams<SLQueryParamConfig>();

  // Internal hooks
  const isIOsStandalone = isIOSStandalone();
  const [showInstallAsStandaloneIOS, _setShowInstallAsStandaloneIOS] = useLocalStorage(
    LocalStorageKeys.SHOW_INSTALL_AS_STANDALONE_IOS,
    isIOsStandalone,
  );
  const setShowInstallAsStandaloneIOS = (value: boolean) => _setShowInstallAsStandaloneIOS(value);

  const [showSignAppWithWallet, setShowSignAppWithWallet] = useState(false);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const toggleMenu = (value?: boolean) => setIsMenuOpen(value ?? !isMenuOpen);

  // Vars
  const showActionMenu = !!landToken && !!action;

  // Effects
  useEffect(() => {
    if (!accountResult.address || !accountResult.isConnected) return;
    setShowSignAppWithWallet(web3AuthSignatureResult.isError);
  }, [web3AuthSignatureResult, accountResult]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <UIStatusContext.Provider
      value={{
        isIOSStandalone: isIOsStandalone,
        showInstallAsStandaloneIOS: !!showInstallAsStandaloneIOS,
        setShowInstallAsStandaloneIOS,
        showSignAppWithWallet,
        setShowSignAppWithWallet,
        isActionMenuOpen: showActionMenu,
        isMenuOpen,
        toggleMenu,
      }}
    >
      {children}
    </UIStatusContext.Provider>
  );
};
